import type { UserAccountType } from "@fraction/shared";
import { useQueryClient } from "@tanstack/react-query";
import cookie from "cookie";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import fraction from "src/api/fraction";
import { useAuthUser } from "src/auth/useAuth";
import { useApplicationsQuery } from "src/hooks/useApplication";
import { useQuery } from "src/lib";
import { saveCookie } from "src/utilities/tracking";
import useEffectOnce from "./useEffectOnce";

const PREFERENCE_COOKIE = "user_preference";
const CHOICES_COOKIE = "user_preference_choices";
const PREFERENCE_QUERY_KEY = ["cookieData", "preferences", "accountView"];
const CHOICES_QUERY_KEY = ["cookieData", "preferences", "accountViewChoices"];

function readPreferenceCookie(): UserAccountType | null {
  const cookies = cookie.parse(document.cookie);
  return (cookies[PREFERENCE_COOKIE] as UserAccountType) || null;
}

function readChoicesCookie(): UserAccountType[] | null {
  const cookies = cookie.parse(document.cookie);
  const raw = cookies[CHOICES_COOKIE];
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as UserAccountType[]) : null;
  } catch {
    return null;
  }
}

export const useAccountViewPreference = () => {
  const { user, userId, isLoadingUser, isLoadingUserError } = useAuthUser();
  const {
    countFunded,
    countActive,
    isLoading: isLoadingCounts,
    isError: isCountError,
  } = useApplicationsQuery({
    refetch: false,
    initialRefetch: false,
    accountType: "applicant",
    status: "all",
    enabled: !!user?.applicantAccounts?.length,
    countOnly: true,
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const liveChoices: UserAccountType[] = useMemo(() => {
    const result: UserAccountType[] = [];
    if (user?.internalEmployee) {
      result.push("employee");
    }
    if (user?.brokerAccount) {
      result.push("broker");
    }
    if (user?.lenderAccount) {
      result.push("lender");
    }
    if (user?.underwritingDeskMember) {
      result.push("desk-agent");
    }
    if (user?.conveyancerAccount) {
      result.push("conveyancer");
    }
    if (countActive) {
      result.push("applicant");
    }
    if (countFunded) {
      result.push("borrower");
    }
    if (user?.servicingCustomer) {
      result.push("servicing");
    }
    return result;
  }, [
    user?.internalEmployee,
    user?.brokerAccount,
    user?.lenderAccount,
    user?.underwritingDeskMember,
    user?.conveyancerAccount,
    user?.servicingCustomer,
    countActive,
    countFunded,
  ]);

  // Cookie reads are synchronous — initialData avoids an unnecessary loading cycle
  const cookieData = useQuery({
    queryKey: PREFERENCE_QUERY_KEY,
    queryFn: readPreferenceCookie,
    initialData: readPreferenceCookie,
  });
  const savedChoicesData = useQuery({
    queryKey: CHOICES_QUERY_KEY,
    queryFn: readChoicesCookie,
    initialData: readChoicesCookie,
  });

  const cookiePreference = cookieData?.data as UserAccountType | null;
  const savedChoices = savedChoicesData?.data ?? null;

  const isLoading = isLoadingUser || isLoadingCounts;

  // `liveChoices` is only trustworthy once we have a fully loaded, error-free
  // view of the user (incl. the application counts). While anything is still
  // loading or a request failed, `liveChoices` is incomplete — so we must NOT
  // conclude a saved preference/choice is invalid. This is the key to not
  // degrading (to "none", or an empty switcher) when an HTTP call fails.
  const choicesAreAuthoritative = !isLoading && !isLoadingUserError && !isCountError && !!user;

  // Available choices the consumer should render: the fresh list when we trust
  // it, otherwise the last-supported list saved from a previous good load.
  const choices = useMemo(
    () => (choicesAreAuthoritative ? liveChoices : (savedChoices ?? liveChoices)),
    [choicesAreAuthoritative, liveChoices, savedChoices]
  );

  const defaultPreference = choices[0];
  const hasMultiple = choices.length > 1;

  // Persist the authoritative choices + computed default into cookies so the
  // rest of the app (which reads them directly) and the next load have a value:
  // - Choices: always saved so the switcher survives reloads / failed fetches.
  // - Preference: single-account users sync their one role; multi-account users
  //   are only seeded when no cookie exists yet.
  // Gated on `choicesAreAuthoritative` so we never persist a partial/failed view.
  useEffectOnce(
    () => {
      saveCookie(CHOICES_COOKIE, JSON.stringify(liveChoices));
      queryClient.setQueryData(CHOICES_QUERY_KEY, liveChoices);

      const shouldSavePreference = !hasMultiple || !cookiePreference;
      if (shouldSavePreference && defaultPreference && defaultPreference !== cookiePreference) {
        saveCookie(PREFERENCE_COOKIE, defaultPreference);
        queryClient.setQueryData(PREFERENCE_QUERY_KEY, defaultPreference);
      }
    },
    [liveChoices, defaultPreference, cookiePreference, hasMultiple, queryClient],
    !choicesAreAuthoritative || liveChoices.length === 0
  );

  const setPreference = useCallback(
    (key: UserAccountType) => {
      saveCookie(PREFERENCE_COOKIE, key);
      queryClient.setQueryData(PREFERENCE_QUERY_KEY, key);
      // Role-scoped endpoints answer as the view we're in, so the switch has to
      // reach the client before the next request goes out (see
      // FractionClient.setAccountType) — and everything already cached was
      // fetched as the old view, so it can't be reused.
      fraction.setAccountType(key);
      void queryClient.invalidateQueries();
      navigate(key === "servicing" ? "/portfolio/analytics" : "/");
    },
    [queryClient, navigate]
  );

  // The cookie is the durable source of truth for the last-supported preference.
  // Keep it whenever data is still unconfirmed (loading/error) or when it's still
  // a valid choice. Once authoritative, a cookie pointing at a role the user no
  // longer has is invalid — fall back to the computed default, and to "none"
  // (which renders the no-account view) when there is truly nothing to show.
  // Crucially, do NOT resurrect the stale cookie here: an account with no roles
  // would otherwise be routed to whatever the cookie last held (e.g. LOS).
  const preference: UserAccountType = useMemo(() => {
    if (cookiePreference && (!choicesAreAuthoritative || choices.includes(cookiePreference))) {
      return cookiePreference;
    }
    // While still unconfirmed, the stale cookie is the best guess; once
    // authoritative we trust only the computed default.
    return defaultPreference ?? (choicesAreAuthoritative ? "none" : (cookiePreference ?? "none"));
  }, [cookiePreference, choicesAreAuthoritative, choices, defaultPreference]);

  // Keep the client's `accountType` in step with the resolved preference —
  // `setPreference` covers the switch itself, this covers first load and the
  // cases where the preference settles from the cookie / computed default.
  useEffect(() => {
    fraction.setAccountType(preference === "none" ? undefined : preference);
  }, [preference]);

  return { userId, preference, setPreference, isLoading, choices };
};
